import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);    // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("immo:auth-expired", onExpired);
    return () => window.removeEventListener("immo:auth-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  };

  // Optimistic: the UI moves on at once, the account catches up. A failed save
  // only means the tour or a checklist tick may show again next login.
  const updateOnboarding = async (patch) => {
    setUser((u) => {
      if (!u) return u;
      const ob = { ...(u.onboarding || {}) };
      if (patch.task) ob.tasks = [...new Set([...(ob.tasks || []), patch.task])];
      for (const k of ["tour", "help_seen", "checklist"]) if (patch[k] !== undefined) ob[k] = patch[k];
      if (patch.tour === "later") ob.later_at = new Date().toISOString();
      return { ...u, onboarding: ob };
    });
    try {
      const { data } = await api.patch("/auth/me/onboarding", patch);
      setUser((u) => (u ? { ...u, onboarding: data } : u));
    } catch { /* keep the optimistic state */ }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, updateOnboarding, formatApiError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
